import React, { useState, useEffect } from 'react';
import { voiceChat } from '@services/VoiceChat';
import { useRoomStore } from '@store/index';
import styles from './VoiceChat.module.css';

export const VoiceChat: React.FC = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
  const [peerVoiceEnabled, setPeerVoiceEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const opponent = useRoomStore((state) => state.opponent);
  
  useEffect(() => {
    // Listen for voice chat events
    const handleTalking = (event: CustomEvent) => {
      setIsTalking(event.detail.talking);
    };
    
    const handleConnectionStatus = (event: CustomEvent) => {
      setConnectionStatus(event.detail.status);
    };
    
    const handlePeerStatus = (event: CustomEvent) => {
      setPeerVoiceEnabled(event.detail.enabled);
    };
    
    const handleError = (event: CustomEvent) => {
      setError(event.detail.error);
      setTimeout(() => setError(null), 5000); // Clear error after 5 seconds
    };
    
    window.addEventListener('voiceChatTalking', handleTalking as EventListener);
    window.addEventListener('voiceChatConnectionStatus', handleConnectionStatus as EventListener);
    window.addEventListener('peerVoiceStatus', handlePeerStatus as EventListener);
    window.addEventListener('voiceChatError', handleError as EventListener);
    
    return () => {
      window.removeEventListener('voiceChatTalking', handleTalking as EventListener);
      window.removeEventListener('voiceChatConnectionStatus', handleConnectionStatus as EventListener);
      window.removeEventListener('peerVoiceStatus', handlePeerStatus as EventListener);
      window.removeEventListener('voiceChatError', handleError as EventListener);
    };
  }, []);
  
  const toggleVoiceChat = async () => {
    if (!opponent) {
      setError('No opponent to connect with');
      return;
    }
    
    if (isEnabled) {
      // Disable voice chat
      voiceChat.disconnect();
      setIsEnabled(false);
      setConnectionStatus('disconnected');
    } else {
      // Enable voice chat
      try {
        await voiceChat.initializeVoiceChat(opponent.id);
        setIsEnabled(true);
      } catch (err) {
        console.error('Failed to enable voice chat:', err);
        setIsEnabled(false);
      }
    }
  };
  
  const getStatusIcon = () => {
    if (!isEnabled) return '🔇';
    if (connectionStatus === 'connected') return '🎤';
    if (connectionStatus === 'connecting') return '⏳';
    return '❌';
  };
  
  const getStatusText = () => {
    if (!isEnabled) return 'Voice Chat Off';
    if (connectionStatus === 'connected') {
      if (isTalking) return 'Talking...';
      return 'Push SPACE to talk';
    }
    if (connectionStatus === 'connecting') return 'Connecting...';
    return 'Connection Failed';
  };
  
  return (
    <div className={styles.voiceChat}>
      <div className={styles.header}>
        <h3 className={styles.title}>Voice Chat</h3>
        <button 
          className={`${styles.toggleButton} ${isEnabled ? styles.enabled : ''}`}
          onClick={toggleVoiceChat}
          disabled={!opponent}
          title={opponent ? 'Toggle voice chat' : 'No opponent connected'}
        >
          {getStatusIcon()}
        </button>
      </div>
      
      <div className={styles.status}>
        <div className={`${styles.statusIndicator} ${isTalking ? styles.talking : ''}`}>
          {getStatusText()}
        </div>
        
        {peerVoiceEnabled && opponent && (
          <div className={styles.peerStatus}>
            {opponent.name} has voice chat enabled
          </div>
        )}
      </div>
      
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}
      
      {isEnabled && connectionStatus === 'connected' && (
        <div className={styles.instructions}>
          <div className={styles.key}>SPACE</div>
          <span>Hold to talk</span>
        </div>
      )}
    </div>
  );
};